/**
 * The winner sheet's editable list (ISSUE-1291): one entry per winner, each
 * a day and a note. Saving sends the whole list for the row in one
 * `/strips/winners-set` call; an entry without a `stripWinnerId` is new and
 * the server pushes its member. The limits (10 winners, 500 characters) are
 * the server's too; these checks only stop a save the server would refuse.
 */
import {
  STRIP_MAX_WINNERS_PER_ROW,
  STRIP_WINNER_NOTE_MAX_LENGTH,
} from "@sportsmet/contest-engine";

import {
  STRIP_WINNER_DATE_REQUIRED,
  STRIP_WINNER_NOTE_REQUIRED,
} from "./stripCardCopy";
import { stripDayOf, stripWonAtFromDay } from "./stripTime";

export { STRIP_MAX_WINNERS_PER_ROW, STRIP_WINNER_NOTE_MAX_LENGTH };

export type typeStripWinnerDraft = {
  /** A stable React key; never sent. */
  key: string;
  stripWinnerId: string | null;
  day: string;
  note: string;
};

export function buildWinnerDrafts(input: {
  winners: readonly { stripWinnerId: string; wonAt: string; note: string }[];
  timeZone: string;
}): typeStripWinnerDraft[] {
  return input.winners.map((winner) => ({
    key: winner.stripWinnerId,
    stripWinnerId: winner.stripWinnerId,
    day: stripDayOf({ iso: winner.wonAt, timeZone: input.timeZone }),
    note: winner.note,
  }));
}

export function newWinnerDraft(input: {
  key: string;
  today: string;
}): typeStripWinnerDraft {
  return { key: input.key, stripWinnerId: null, day: input.today, note: "" };
}

export type typeWinnerDraftErrors = Record<
  string,
  { day?: string; note?: string }
>;

/** Field errors by draft key; empty when every entry can be saved. */
export function winnerDraftErrors(
  drafts: readonly typeStripWinnerDraft[],
): typeWinnerDraftErrors {
  const errors: typeWinnerDraftErrors = {};
  for (const draft of drafts) {
    const entry: { day?: string; note?: string } = {};
    if (stripWonAtFromDay(draft.day) === null) {
      entry.day = STRIP_WINNER_DATE_REQUIRED;
    }
    if (draft.note.trim() === "") entry.note = STRIP_WINNER_NOTE_REQUIRED;
    if (entry.day !== undefined || entry.note !== undefined) {
      errors[draft.key] = entry;
    }
  }
  return errors;
}

/** The `winners` array `/strips/winners-set` takes, or null while any entry
 *  has an error. */
export function winnerDraftsBody(
  drafts: readonly typeStripWinnerDraft[],
): { stripWinnerId: string | null; wonAt: string; note: string }[] | null {
  if (Object.keys(winnerDraftErrors(drafts)).length > 0) return null;
  const entries: {
    stripWinnerId: string | null;
    wonAt: string;
    note: string;
  }[] = [];
  for (const draft of drafts) {
    const wonAt = stripWonAtFromDay(draft.day);
    if (wonAt === null) return null;
    entries.push({
      stripWinnerId: draft.stripWinnerId,
      wonAt,
      note: draft.note.trim().slice(0, STRIP_WINNER_NOTE_MAX_LENGTH),
    });
  }
  return entries;
}

/** True when the drafts differ from what the server holds for the row. */
export function isWinnerDraftDirty(input: {
  initial: readonly typeStripWinnerDraft[];
  drafts: readonly typeStripWinnerDraft[];
}): boolean {
  if (input.initial.length !== input.drafts.length) return true;
  return input.drafts.some((draft, index) => {
    const before = input.initial[index];
    return (
      before === undefined ||
      before.stripWinnerId !== draft.stripWinnerId ||
      before.day !== draft.day ||
      before.note !== draft.note
    );
  });
}
